import { createFileRoute } from "@tanstack/react-router";
import heroBg from "@/assets/heroes/colorado-digital-marketing-hero.jpg";
import { Sparkles } from "lucide-react";
import { StatewideServiceHub } from "@/components/StatewideServiceHub";
import { buildCityCards } from "@/lib/statewideServiceCities";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { buildServiceHubScripts } from "@/lib/serviceHubSchema";

const CANONICAL = "https://coloradowebimpressions.com/digital-marketing";
const OG_IMAGE = `https://coloradowebimpressions.com${ogImageFor({ service: "seo" })}`;

export const Route = createFileRoute("/digital-marketing")({
  head: () => ({
    meta: [
      { title: "Colorado Digital Marketing — Statewide, Owner-Led Since 2006" },
      {
        name: "description",
        content:
          "One Colorado agency for SEO, web design, Google Ads, Maps & GEO AI. Helping Colorado businesses succeed online, owner-led since 2006. Free 24-hr audit.",
      },
      { property: "og:title", content: "Colorado Digital Marketing — Statewide, Owner-Led Since 2006" },
      { property: "og:description", content: "One Colorado agency for SEO, web, ads, Maps & AI. Helping Colorado businesses succeed online since 2006." },
      { property: "og:url", content: CANONICAL },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: "Colorado Digital Marketing — Since 2006",
        description: "Helping Colorado businesses succeed online.",
      }),
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: buildServiceHubScripts({
      path: "/digital-marketing",
      name: "Digital Marketing",
      description: "Full-service Colorado digital marketing — SEO, web design, Google Ads, Google Maps, and GEO AI — for businesses across Colorado.",
      serviceType: "Digital Marketing",
      priceRange: "$$",
      faqs: [
        { q: "How much does Colorado digital marketing cost?", a: "Monthly marketing starts at $950/month for Local SEO and Google Maps. Website builds are separate and start at $1,850 one-time. Visit our pricing page to compare all packages and build a plan." },
        { q: "Do you require a long-term contract?", a: "No. Every engagement is month-to-month with no early-termination fees. We re-earn the next month with the current month's results." },
        { q: "Which Colorado cities do you serve?", a: "We work with businesses across the entire state — Denver, Colorado Springs, Boulder, Fort Collins, Pueblo, Aurora, Castle Rock, Parker, Monument, Greeley, and surrounding markets." },
        { q: "How long until I see results?", a: "Google Ads delivers leads in week one. Local SEO and Map Pack movement typically show in 60–120 days. Statewide SEO compounds over 6–12 months." },
      ],
    }),
  }),
  component: () => (
    <StatewideServiceHub
      heroImage={{ src: heroBg, alt: "Colorado digital marketing and SEO analytics dashboard with Pikes Peak view — Colorado Web Impressions" }}
      service="Digital Marketing"
      serviceLower="digital marketing"
      eyebrow="Digital Marketing Colorado"
      headline="Helping Colorado Businesses Succeed Online"
      subheadline="SEO, web design, Google Ads, Google Maps, and GEO AI for businesses across Colorado. One owner-led Colorado agency helping businesses get found, earn trust and win more customers."
      benefits={[
        "One agency for SEO, web, ads, Maps, and AI search",
        "Owner-led — direct access to Chris, no handoff",
        "Strategy built around your customers and goals",
        "Published pricing — apples-to-apples comparison",
        "100+ Colorado businesses across the Front Range",
      ]}
      benefitIcon={Sparkles}
      servicesGrid
      industriesGrid
      cities={buildCityCards("seo", "/colorado-springs")}
      pickerHeading="Proudly serving businesses across Colorado"
      closing="Outside the cities listed? We build custom digital marketing programs for any Colorado market. Reach out and we'll scope it."
    />
  ),
});
